import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptions,
  ValidateIbanResponse,
} from 'types/beneficiaries'
import { ContactFiatInstrumentFormKeys } from '../schemas/contactFiatInstrumentSchema'
import { FieldsFetchedFromIbanValidation, Step, Steps } from '../types/fiatInstruments'

export const isAddressFieldsRequired = ({
  currency,
  transferDestination,
  paymentMethod,
}: {
  currency?: string
  transferDestination?: string
  paymentMethod?: string
}) => {
  const isSwift = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isLocalAchOrFedwire =
    transferDestination === BeneficiaryTransferDestination.LOCAL &&
    currency === BeneficiaryDetailsCurrency.USD &&
    paymentMethod !== USDBeneficiaryOptions.CUBIX

  return isSwift || isLocalAchOrFedwire
}

export const getFieldsToValidate = (step: Step): ContactFiatInstrumentFormKeys[] => {
  const basicFields: ContactFiatInstrumentFormKeys[] = [
    'transferDestination',
    'entityType',
    'description',
    'currency',
    'paymentReference',
    'beneficiaryAddressLine1',
    'beneficiaryAddressLine2',
    'beneficiaryCity',
    'beneficiaryRegion',
    'beneficiaryPostCode',
    'beneficiaryCountryCode',
  ]

  const basicBankAccountFields: ContactFiatInstrumentFormKeys[] = [
    'beneficiaryBankName',
    'accountNumber',
    'bankCode',
    'routingNumber',
    'accountType',
    'paymentMethod',
  ]
  const bankAddressFields: ContactFiatInstrumentFormKeys[] = [
    'bankName',
    'bankCountryName',
    'bankAddressLine1',
    'bankAddressLine2',
    'bankCity',
    'bankRegion',
    'bankPostCode',
    'bankCountryCode',
  ]
  const intermediaryBankFields: ContactFiatInstrumentFormKeys[] = [
    'intermediaryBankCode',
    'intermediaryBankRoutingNumber',
    'intermediaryBankName',
    'intermediaryBankAddressLine1',
    'intermediaryBankAddressLine2',
    'intermediaryBankCity',
    'intermediaryBankRegion',
    'intermediaryBankPostCode',
    'intermediaryBankCountryCode',
  ]

  switch (step) {
    case Steps.LOCAL_BANK_DETAILS:
      return [...basicFields, ...basicBankAccountFields]
    case Steps.INTERNATIONAL_BANK_DETAILS:
      return [
        ...basicFields,
        ...basicBankAccountFields,
        ...bankAddressFields,
        ...intermediaryBankFields,
      ]
    case Steps.INTERMEDIARY_BANK_DETAILS:
      return intermediaryBankFields
    default:
      return []
  }
}

export const getIsValidIban = ({
  currency,
  ibanBankDetails,
  ibanSupportedPaymentMethods,
}: {
  currency?: string
  ibanBankDetails?: ValidateIbanResponse
  ibanSupportedPaymentMethods?: string[]
}) => {
  switch (currency) {
    case 'AED':
      return !!ibanBankDetails?.bankAccount?.bankCode && !ibanBankDetails?.validationFailed?.length
    case 'EUR':
      return (
        (ibanSupportedPaymentMethods?.includes('SEPA_CT') ||
          ibanSupportedPaymentMethods?.includes('SEPA_INST')) &&
        !ibanBankDetails?.validationFailed?.length
      )
    default:
      return false
  }
}

export const cleanIbanValidationResponse = (
  bankDetails: ValidateIbanResponse
): ValidateIbanResponse => {
  return {
    bankAccount: {
      type: bankDetails?.bankAccount?.type,
      bankName: bankDetails?.bankAccount?.bankName?.trim(),
      accountNumber: bankDetails?.bankAccount?.accountNumber?.trim(),
      bankCode: bankDetails?.bankAccount?.bankCode?.trim(),
      bankAddress: {
        addressLine1: bankDetails?.bankAccount?.bankAddress?.addressLine1?.trim(),
        addressLine2: bankDetails?.bankAccount?.bankAddress?.addressLine2?.trim(),
        city: bankDetails?.bankAccount?.bankAddress?.city?.trim(),
        region: bankDetails?.bankAccount?.bankAddress?.region?.trim(),
        postCode: bankDetails?.bankAccount?.bankAddress?.postCode?.trim(),
        countryCode: bankDetails?.bankAccount?.bankAddress?.countryCode?.trim(),
      },
    },
    validationFailed: bankDetails?.validationFailed,
    supportedPaymentMethods: bankDetails?.supportedPaymentMethods,
  }
}

export const setFieldsFetchedFromIbanValidation = (
  ibanValidationResponse: ValidateIbanResponse
): FieldsFetchedFromIbanValidation => {
  const cleanDetail = cleanIbanValidationResponse(ibanValidationResponse)

  if (!cleanDetail?.validationFailed?.length) {
    const { bankCode, bankName, bankAddress, accountNumber } = cleanDetail?.bankAccount || {}

    return {
      ...(accountNumber && {
        accountNumber: accountNumber,
      }),
      ...(bankCode && {
        bankCode: bankCode,
      }),
      ...(bankName && {
        bankName: bankName,
      }),
      ...(bankAddress?.addressLine1 && {
        bankAddressLine1: bankAddress?.addressLine1,
      }),
      ...(bankAddress?.addressLine2 && {
        bankAddressLine2: bankAddress?.addressLine2,
      }),
      ...(bankAddress?.city && {
        bankCity: bankAddress?.city,
      }),
      ...(bankAddress?.postCode && {
        bankPostCode: bankAddress?.postCode,
      }),
      ...(bankAddress?.region && {
        bankRegion: bankAddress?.region,
      }),
    }
  }
}
